import {Filter, type FilterProps} from '@github-ui/filter'
import {LabelFilterProvider, MilestoneFilterProvider} from '@github-ui/filter/providers'
import {IssueTypeFilterProvider} from '@github-ui/issue-type-filter-provider'
import {type ColorName, type ColorSet, useGetPresentationalColor} from '@github-ui/use-named-color'
import {memo, useCallback, useImperativeHandle, useMemo, useRef} from 'react'
import {type Environment, useRelayEnvironment} from 'react-relay'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {assertNever} from '../../helpers/assert-never'
import {getInitialState} from '../../helpers/initial-state'
import type {LoggedInUser} from '../../helpers/json-island'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {isFilterableColumn} from '../../models/column-capabilities'
import type {ColumnModel} from '../../models/column-model'
import {useAllColumns} from '../../state-providers/columns/use-all-columns'
import {Resources} from '../../strings'
import styles from './filter-bar.module.css'
import {
  BlockedByFilterProvider,
  BlockingFilterProvider,
  DateFilterProvider,
  FILTER_PRIORITIES,
  IterationFilterProvider,
  LastUpdatedFilterProvider,
  LinkedPullRequestsFilterProvider,
  MemexAssigneeFilterProvider,
  MemexIsFilterProvider,
  MemexLabelFilterProvider,
  MemexMilestoneFilterProvider,
  MemexParentIssueFilterProvider,
  MemexRepositoryFilterProvider,
  MemexReviewersFilterProvider,
  MemexStateFilterProvider,
  MemexUpdatedFilterProvider,
  NumberFilterProvider,
  ReasonFilterProvider,
  SingleSelectFilterProvider,
  SubIssuesProgressFilterProvider,
  TextFilterProvider,
  TitleFilterProvider,
} from './filter-providers'
import {useHandleFilterBarShortcut} from './use-handle-filter-bar-shortcut'

export type FilterBarProps = Pick<
  FilterProps,
  'filterValue' | 'onChange' | 'context' | 'onValidation' | 'onKeyDown' | 'onSubmit' | 'trailingVisual'
> & {
  /**
   * If provided, only these filter providers will be used.
   * This is useful for limiting filter support for a specific context like a workflow.
   */
  limitedProviderKeys?: Array<string>
  /**
   * An optional ref for the input element.
   */
  inputRef?: React.RefObject<HTMLInputElement | null>
}

type GetFilterProviderForColumnParams = {
  column: ColumnModel
  getPresentationalColor: (color: ColorName) => ColorSet
  loggedInUser?: LoggedInUser
  relayEnvironment?: Environment
  projectOwnerLogin?: string
  projectNumber?: number
  context?: FilterProps['context']
}

/**
 * Resolves column name collisions by preferring issue fields over project fields.
 * This handles the edge case where a user has a project field with a name, and then
 * an issue field gets renamed to the same name. Issue fields take precedence.
 */
export const resolveColumnNameCollisions = (columns: Array<ColumnModel>): Array<ColumnModel> => {
  const columnMap = new Map<string, ColumnModel>()
  for (const column of columns) {
    const existing = columnMap.get(column.name)
    if (!existing) {
      columnMap.set(column.name, column)
    } else if (column.isIssueField && !existing.isIssueField) {
      columnMap.set(column.name, column)
    }
  }
  return Array.from(columnMap.values())
}

const getFilterProviderForColumn = ({
  column,
  getPresentationalColor,
  loggedInUser,
  relayEnvironment,
  projectOwnerLogin,
  projectNumber,
  context,
}: GetFilterProviderForColumnParams) => {
  const userFilterParams = loggedInUser
    ? {
        showAtMe: true,
        currentUserLogin: loggedInUser.login,
        currentUserAvatarUrl: loggedInUser.avatarUrl,
      }
    : {showAtMe: false}

  if (!isFilterableColumn(column.dataType)) {
    return undefined
  }

  const {dataType} = column

  switch (dataType) {
    case MemexColumnDataType.Assignees:
      return new MemexAssigneeFilterProvider(column, userFilterParams, {
        filterTypes: {exclusive: true, hasValue: true},
      })
    case MemexColumnDataType.Date:
      return new DateFilterProvider(column)
    case MemexColumnDataType.Iteration:
      return new IterationFilterProvider(column)
    case MemexColumnDataType.Labels: {
      const options = {
        filterTypes: {exclusive: true, hasValue: true},
      }
      if (context && context['repo']) {
        // Suggests and validates labels for a specific repository
        return new LabelFilterProvider(options)
      }
      // Suggests labels across all repositories in the project; no validation.
      return new MemexLabelFilterProvider(column, options)
    }
    case MemexColumnDataType.LinkedPullRequests:
      return new LinkedPullRequestsFilterProvider({
        filterTypes: {hasValue: true},
      })
    case MemexColumnDataType.Milestone: {
      const options = {
        priority: FILTER_PRIORITIES.milestone,
        filterTypes: {exclusive: true, hasValue: true},
      }
      if (context && context['repo']) {
        // Suggests and validates milestones for a specific repository
        return new MilestoneFilterProvider(options)
      }
      // Suggests milestones across all repositories in the project; no validation.
      return new MemexMilestoneFilterProvider(column, options)
    }
    case MemexColumnDataType.IssueType: {
      // Suggests and validates issue types for a specific repository
      const repoScope = context ? context['repo'] : undefined
      // Fall back to suggesting issue types from the project's organization if we aren't suggesting them from a particular repo (and if we have the necessary context to do so)
      const projectScope = repoScope ? undefined : {login: projectOwnerLogin, projectNumber}
      return new IssueTypeFilterProvider(
        {priority: FILTER_PRIORITIES.type, filterTypes: {valueless: true, hasValue: true}},
        false,
        relayEnvironment,
        repoScope,
        projectScope,
      )
    }
    case MemexColumnDataType.Number:
      return new NumberFilterProvider(column)
    case MemexColumnDataType.Repository:
      return new MemexRepositoryFilterProvider(column, {filterTypes: {exclusive: true, hasValue: true}})
    case MemexColumnDataType.Reviewers:
      return new MemexReviewersFilterProvider(userFilterParams, {
        filterTypes: {exclusive: true, hasValue: true},
      })
    case MemexColumnDataType.SingleSelect:
      return new SingleSelectFilterProvider(column, getPresentationalColor)
    case MemexColumnDataType.SubIssuesProgress:
      return new SubIssuesProgressFilterProvider(column, {filterTypes: {hasValue: true}})
    case MemexColumnDataType.Text:
      return new TextFilterProvider(column)
    case MemexColumnDataType.Title:
      return new TitleFilterProvider(column)
    case MemexColumnDataType.ParentIssue:
      return new MemexParentIssueFilterProvider(column, {filterTypes: {hasValue: true}})
    default: {
      return assertNever(dataType)
    }
  }
}

export const FilterBar = memo(function FilterBar({
  filterValue,
  onChange,
  context,
  onValidation,
  inputRef: givenInputRef,
  onKeyDown,
  limitedProviderKeys,
  trailingVisual,
}: FilterBarProps) {
  const relayEnvironment = useRelayEnvironment()

  const {memex_filter_on_key_down} = useEnabledFeatures()
  const {allColumns} = useAllColumns()
  const {loggedInUser, projectOwner, projectData} = getInitialState()
  const {getPresentationalColor} = useGetPresentationalColor()

  const columnFilterProviders = useMemo(() => {
    const preferredColumns = resolveColumnNameCollisions(allColumns)
    return preferredColumns
      .map(column =>
        getFilterProviderForColumn({
          column,
          getPresentationalColor,
          loggedInUser,
          relayEnvironment,
          projectOwnerLogin: projectOwner?.login,
          projectNumber: projectData?.number,
          context,
        }),
      )
      .filter(p => !!p)
  }, [
    allColumns,
    getPresentationalColor,
    loggedInUser,
    relayEnvironment,
    projectOwner?.login,
    projectData?.number,
    context,
  ])

  const issueDependenciesFilterProviders = useMemo(() => {
    return [
      new BlockedByFilterProvider({filterTypes: {hasValue: true}}),
      new BlockingFilterProvider({filterTypes: {hasValue: true}}),
    ]
  }, [])

  const providers = useMemo(() => {
    const allProviders = [
      ...columnFilterProviders,
      new LastUpdatedFilterProvider(),
      new MemexIsFilterProvider(['issue', 'pr', 'open', 'closed', 'draft', 'merged', 'blocked', 'blocking'], {
        filterTypes: {valueless: false},
      }),
      new MemexStateFilterProvider('mixed', {
        priority: FILTER_PRIORITIES.state,
        filterTypes: {valueless: false},
      }),
      new MemexUpdatedFilterProvider({filterTypes: {valueless: false}}),
      new ReasonFilterProvider(),
      ...issueDependenciesFilterProviders,
    ]
    if (limitedProviderKeys) {
      return allProviders.filter(provider => limitedProviderKeys.includes(provider.key))
    } else {
      return allProviders
    }
  }, [columnFilterProviders, issueDependenciesFilterProviders, limitedProviderKeys])

  const inputRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(memex_filter_on_key_down ? givenInputRef : undefined, () => inputRef.current)

  const onFilterBarShortcut = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  useHandleFilterBarShortcut(onFilterBarShortcut)

  return (
    <Filter
      id="filter-bar-component"
      data-testid="filter-bar-component"
      label="Filter"
      filterButtonVariant="compact"
      settings={{
        aliasMatching: true,
      }}
      variant="input"
      aria-label={Resources.filterByKeyboardOrByField}
      placeholder={Resources.filterByKeyboardOrByField}
      filterValue={filterValue}
      inputRef={inputRef}
      onChange={onChange}
      onValidation={onValidation}
      onKeyDown={memex_filter_on_key_down ? onKeyDown : undefined}
      context={context}
      providers={providers}
      className={styles.Filter_0}
      trailingVisual={trailingVisual}
    />
  )
})

try{ FilterBar.displayName ||= 'FilterBar' } catch {}